import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VideoCard from '../components/VideoCard';
import SEO from '../components/SEO';
import { MobileAdCategories } from '../components/AdBanner';
import { Loader2, X, Search, Plus, SlidersHorizontal, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Popular tags for quick selection
const POPULAR_TAGS = [
  'Amateur', 'MILF', 'Teen', 'Blonde', 'Brunette', 'Redhead', 'Asian', 'Latina', 'Ebony',
  'BBW', 'Petite', 'Curvy', 'Big Tits', 'Small Tits', 'Big Ass', 'Anal', 'Oral',
  'Creampie', 'POV', 'Hardcore', 'Softcore', 'Solo', 'Lesbian', 'Threesome', 'Group',
  'Outdoor', 'Public', 'Massage', 'Cosplay', 'BDSM', 'Feet', 'Lingerie', 'Stockings',
  'Interracial', 'Stepmom', 'College', 'Mature', 'Vintage', 'Japanese', 'Korean',
  'Indian', 'Arab', 'Czech', 'German', 'French', 'British', 'Russian',
  'Squirt', 'Deepthroat', 'Facial', 'Swallow', 'Double Penetration',
  'Gangbang', 'Orgy', 'Cuckold', 'Hotwife', 'Voyeur', 'Hidden Cam'
];

const SORT_OPTIONS = [
  { value: 'mostviewed', label: 'Most Viewed' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'newest', label: 'Newest' },
];

const PERIOD_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'weekly', label: 'This Week' },
  { value: 'monthly', label: 'This Month' },
];

const Categories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTags, setActiveTags] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sorting, setSorting] = useState('mostviewed');
  const [period, setPeriod] = useState('');
  const [showAllTags, setShowAllTags] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Load tags from URL on mount
  useEffect(() => {
    const urlTags = searchParams.get('tags');
    if (urlTags) {
      setActiveTags(urlTags.split(',').filter(Boolean));
    }
  }, []);

  // Fetch videos whenever tags, sorting, or period changes
  useEffect(() => {
    setPage(1);
    setVideos([]);
    fetchVideos(1, true);
  }, [activeTags, sorting, period]);

  const fetchVideos = async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        ordering: sorting,
      };
      
      if (activeTags.length > 0) {
        params.tags = activeTags.join(',');
      }
      if (period) {
        params.period = period;
      }

      const response = await axios.get(`${API}/content/videos`, { params });
      const newVideos = response.data.videos || [];
      setTotalCount(response.data.count || 0);
      
      if (reset) {
        setVideos(newVideos);
      } else {
        setVideos(prev => [...prev, ...newVideos]);
      }
      
      setHasMore(newVideos.length >= 20);
    } catch (error) {
      console.error('Error fetching videos:', error);
      if (reset) setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const addTag = (tag) => {
    if (!activeTags.includes(tag)) {
      const newTags = [...activeTags, tag];
      setActiveTags(newTags);
      updateURL(newTags);
    }
  };

  const removeTag = (tag) => {
    const newTags = activeTags.filter(t => t !== tag);
    setActiveTags(newTags);
    updateURL(newTags);
  };

  const clearAllTags = () => {
    setActiveTags([]);
    updateURL([]);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    const trimmed = customInput.trim();
    if (trimmed && !activeTags.includes(trimmed)) {
      addTag(trimmed);
    }
    setCustomInput('');
  };

  const updateURL = (tags) => {
    if (tags.length > 0) {
      setSearchParams({ tags: tags.join(',') });
    } else {
      setSearchParams({});
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVideos(nextPage, false);
  };

  const displayedTags = showAllTags ? POPULAR_TAGS : POPULAR_TAGS.slice(0, 24);

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8 bg-[#050505]" data-testid="categories-page">
      <SEO 
        title={activeTags.length > 0 ? `${activeTags.join(' + ')} Videos` : "Explore Categories"} 
        description="Explore 150+ adult video categories on VelvetStream. Combine tags to find exactly what you want."
        path="/categories"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase mb-3 text-white"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="categories-title"
          >
            {activeTags.length > 0 ? 'Search Results' : 'Explore & Discover'}
          </h1>
          <p className="text-gray-500 text-sm max-w-lg mx-auto">
            Combine multiple tags to narrow your search. The more you add, the more specific your results.
          </p>
        </div>

        {/* Search Input */}
        <div className="max-w-2xl mx-auto mb-6">
          <form onSubmit={handleCustomSubmit} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Type a keyword and press Enter to add..."
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg pl-12 pr-24 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/50 transition-colors"
              data-testid="tag-search-input"
            />
            <button
              type="submit"
              disabled={!customInput.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium disabled:opacity-30 hover:bg-purple-700 transition-colors"
              data-testid="add-tag-btn"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Add
            </button>
          </form>
        </div>

        {/* Active Tags */}
        {activeTags.length > 0 && (
          <div className="max-w-4xl mx-auto mb-6" data-testid="active-tags">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500 text-sm">Active filters:</span>
              {activeTags.map((tag) => (
                <span 
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded-full text-purple-300 text-sm"
                >
                  {tag}
                  <button 
                    onClick={() => removeTag(tag)}
                    className="ml-1 hover:text-white transition-colors"
                    data-testid={`remove-tag-${tag}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button 
                onClick={clearAllTags}
                className="text-xs text-gray-500 hover:text-red-400 transition-colors ml-2"
                data-testid="clear-all-tags"
              >
                Clear all
              </button>
              {totalCount > 0 && (
                <span className="text-xs text-gray-600 ml-auto">{totalCount}+ results</span>
              )}
            </div>
          </div>
        )}

        {/* Filters Toggle */}
        <div className="max-w-4xl mx-auto mb-4 flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
            data-testid="toggle-filters"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Sort & Filter
            <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Sort & Period Filters */}
        {showFilters && (
          <div className="max-w-4xl mx-auto mb-6 flex flex-wrap gap-4 p-4 bg-[#0E0E0E] border border-white/5 rounded-lg">
            <div>
              <label className="text-xs text-gray-500 uppercase mb-1 block">Sort by</label>
              <div className="flex gap-1">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setSorting(opt.value)}
                    className={`px-3 py-1.5 rounded text-xs transition-all ${
                      sorting === opt.value ? 'bg-purple-600 text-white' : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/5'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase mb-1 block">Time period</label>
              <div className="flex gap-1">
                {PERIOD_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPeriod(opt.value)}
                    className={`px-3 py-1.5 rounded text-xs transition-all ${
                      period === opt.value ? 'bg-purple-600 text-white' : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/5'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Popular Tags Cloud */}
        <div className="max-w-4xl mx-auto mb-8">
          <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Quick tags — click to combine:</p>
          <div className="flex flex-wrap gap-2">
            {displayedTags.map((tag) => (
              <button
                key={tag}
                onClick={() => activeTags.includes(tag) ? removeTag(tag) : addTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeTags.includes(tag)
                    ? 'bg-purple-600 text-white scale-105'
                    : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/10 border border-white/5 hover:border-purple-500/30'
                }`}
                data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tag}
              </button>
            ))}
          </div>
          {!showAllTags && (
            <button
              onClick={() => setShowAllTags(true)}
              className="mt-3 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              Show all {POPULAR_TAGS.length} tags...
            </button>
          )}
        </div>

        {/* Mobile Ad */}
        <MobileAdCategories />

        {/* Video Results */}
        <div className="mt-8">
          {loading && videos.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : videos.length === 0 && activeTags.length > 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg mb-2">No results for this combination</p>
              <p className="text-gray-600 text-sm">Try removing some tags or using different keywords</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg mb-2">Select tags above to start searching</p>
              <p className="text-gray-600 text-sm">Or type a custom keyword in the search box</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {videos.map((video, index) => (
                  <VideoCard key={`${video.id}-${index}`} video={video} />
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="px-8 py-3 bg-[#1A1A1A] border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="load-more-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Categories;
